import {Component} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import UserModel from '../../services/user.model';
import {UserService} from '../../services/user.service';
import {ActivatedRoute, Router} from '@angular/router';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import {SessionService} from '../../services/session.service';
import SessionModel from '../../services/session.model';

/**
 * @component ForgotPasswordStep1Component
 * @description Component responsible for the first step of the password recovery process.
 * Handles mobile number verification and OTP generation/validation. Users enter their registered
 * mobile number to receive an OTP, which they then verify before proceeding to password reset.
 * Includes countdown timers for OTP expiration and resend functionality.
 * 
 * @selector app-forgot-password-step1
 * @template ./forgot-password-step1.component.html
 * @styleUrl ./forgot-password-step1.component.scss
 */
@Component({
  selector: 'app-forgot-password-step1',
  templateUrl: './forgot-password-step1.component.html',
  styleUrls: ['./forgot-password-step1.component.scss']
})
export class ForgotPasswordStep1Component {
  /**
   * @property {FormGroup} forgotForm
   * @description FormGroup managing the mobile number input field for password recovery.
   * Contains validation for required mobile number field.
   */
  public forgotForm: FormGroup;

  /**
   * @property {FormGroup} otpForm
   * @description FormGroup managing the OTP input field for verification.
   * Contains validation for required OTP field.
   */
  public otpForm: FormGroup;

  /**
   * @property {boolean} submitted
   * @description Flag indicating whether the mobile number form has been submitted.
   * Used to control visibility of validation errors for the forgot form.
   */
  public submitted = false;

  /**
   * @property {boolean} otpSubmitted
   * @description Flag indicating whether the OTP verification form has been submitted.
   * Used to control visibility of validation errors for the OTP form.
   */
  public otpSubmitted = false;

  /**
   * @type {UserModel} userDetails
   * @description Model containing user details retrieved from the backend,
   * including person information, dialing code, and user name.
   */
  public userDetails: UserModel;

  /**
   * @type {UserModel} otpDetails
   * @description Model containing OTP response details from the forgot password API call.
   */
  public otpDetails: UserModel;

  /**
   * @type {UserModel} otpVerifyDetails
   * @description Model containing OTP verification details submitted by the user.
   */
  public otpVerifyDetails: UserModel;

  /**
   * @type {SessionModel} sessionModel
   * @description Model containing guest session information for API authentication.
   */
  public sessionModel: SessionModel;

  /**
   * @property {boolean} hideMobileNumberform
   * @description Flag to toggle visibility of the mobile number input form.
   * Hidden after OTP is sent successfully.
   */
  public hideMobileNumberform: boolean = false;

  /**
   * @property {boolean} otpFormDisplay
   * @description Flag to control visibility of the OTP verification form.
   * Displayed after OTP is sent to user's mobile.
   */
  public otpFormDisplay: boolean;

  /**
   * @property {any} startingMinutes
   * @description Initial countdown duration in minutes for OTP timer (typically 2 minutes).
   */
  public startingMinutes: any;

  /**
   * @property {any} time
   * @description Current countdown time in seconds for OTP expiration timer.
   */
  public time: any;

  /**
   * @property {any} countDownEle
   * @description DOM element or string displaying the OTP countdown timer.
   */
  public countDownEle: any;

  /**
   * @property {boolean} disabledResendOtp
   * @description Flag to disable the resend OTP button during active countdown.
   */
  public disabledResendOtp: boolean;

  /**
   * @property {boolean} hideTimer
   * @description Flag to hide the OTP countdown timer from the UI.
   */
  public hideTimer: boolean;

  /**
   * @property {any} resendstartingMinutes
   * @description Initial countdown duration for resend OTP timer in minutes.
   */
  public resendstartingMinutes: any;

  /**
   * @property {any} resendtime
   * @description Current countdown time in seconds for resend OTP timer.
   */
  public resendtime: any;

  /**
   * @property {any} resendcountDownEle
   * @description DOM element or string displaying the resend OTP countdown timer.
   */
  public resendcountDownEle: any;

  /**
   * @property {boolean} resendhideTimer
   * @description Flag to hide the resend OTP countdown timer from the UI.
   */
  public resendhideTimer: boolean;

  /**
   * @method constructor
   * @description Initializes the component with required Angular services and creates
   * form groups for mobile number and OTP input with appropriate validators.
   * 
   * @param {SessionService} sessionService - Service for managing user sessions
   * @param {FormBuilder} formBuilder - Service for building and managing FormGroup instances
   * @param {UserService} userService - Service for user-related operations including forgot password
   * @param {Router} router - Service for navigating between routes
   * @param {ActivatedRoute} route - Service for accessing route parameters
   * @param {LocationStrategy} locationStrategy - Service for getting current route location
   * @param {PreviousRouteService} previousRouteService - Service for tracking previous navigation route
   */
  constructor(public sessionService: SessionService, public formBuilder: FormBuilder, public userService: UserService, public router: Router, public route: ActivatedRoute,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.userDetails = new UserModel();
    this.otpDetails = new UserModel();
    this.otpVerifyDetails = new UserModel();
    /*validation*/
    this.forgotForm = this.formBuilder.group({
      mobilenumber: ['', Validators.required],
    });
    /*validation end*/
    /*otp validation*/
    this.otpForm = this.formBuilder.group({
      otp: ['', [Validators.required
      ]],
    });
    /*otp validation end*/
  }

  /**
   * @method ngOnInit
   * @description Angular lifecycle hook called when the component is initialized.
   * Performs guest login, initializes form states, and sets up countdown timer elements.
   * 
   * @returns {void}
   */
  public ngOnInit(): void {
    this.onLogin();
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.hideMobileNumberform = true;
    this.startingMinutes = 2;
    this.time = this.startingMinutes * 60;
    this.countDownEle = document.getElementById('time');
    this.disabledResendOtp = false;
    this.hideTimer = false;
    this.resendhideTimer = false;
    this.resendstartingMinutes = 2;
    this.resendtime = this.resendstartingMinutes * 60;
    this.resendcountDownEle = document.getElementById('timer');
  }

  /**
   * @method onLogin
   * @description Performs guest login to obtain session tokens required for API authentication.
   * Stores guest login data in localStorage for subsequent API calls.
   * 
   * @async
   * @returns {Promise<void>}
   */
  async onLogin() { // guest login for tokens
    const headers = {};
    headers['Content-Type'] = 'application/json';
    let userName = 'guest';
    let password = '$2a$10$sl0Lf6zgnyZ9cS/MKidFgOLbmOpOhtjZgf/OWxTCQRXku9qaxNBTS';
    this.sessionModel = await this.sessionService.sessionLogin(userName, password, 'GUEST');
    localStorage.setItem('guestLoginData', JSON.stringify(this.sessionModel.user));
  }

  /**
   * @method f
   * @description Getter providing convenient access to form controls of the forgotForm.
   * Used in the template for form validation and error handling.
   * 
   * @returns {AbstractControlCollection} Form controls object of the forgot password form
   */
  public get f() {
    return this.forgotForm.controls;
  }

  /**
   * @method f1
   * @description Getter providing convenient access to form controls of the otpForm.
   * Used in the template for OTP form validation and error handling.
   * 
   * @returns {AbstractControlCollection} Form controls object of the OTP verification form
   */
  public get f1() {
    return this.otpForm.controls;
  }

  /**
   * @method sendOtp
   * @description Handles the OTP sending process. Validates mobile number, retrieves user details,
   * verifies user eligibility (Business Admin, Farmer, Provider, etc.), and sends OTP to registered mobile.
   * Displays OTP form and starts countdown timer on success.
   * 
   * @async
   * @returns {Promise<boolean|void>} Returns false if user validation fails, otherwise void
   * @throws {Error} Catches and alerts any service errors
   */
  public async sendOtp() {
    this.hideTimer = true;
    this.resendhideTimer = false;
    try {
      this.submitted = true;
      // stop here if form is invalid
      if (this.forgotForm.invalid) {
        return;
      }
      this.userDetails = await this.userService.getUserDetailsByNumber(this.userDetails.userName);
      if (this.userDetails) {
        if (this.userDetails.Persons.length > 0 && [undefined, null, 'undefined', 'N.A.'].includes(this.userDetails.Persons[0]['dialingCode'])) {
          alert('Unable to reset the password due to address not found for the provided mobile number please contact Customer support');
          return false;
        } else if(this.userDetails.Persons.length > 0 && !['Business Admin', 'Farmer', 'Farm Manager', 'Provider', 'Provider Manager'].includes(this.userDetails.Persons[0]['personType'][0]['name'])) {
          alert('Unable to reset the password because provided mobile number is not an Farm or Provider business please contact Customer support');
          return false;
        } else {
          this.otpDetails = await this.userService.forgotPassword(this.userDetails.Persons[0].dialingCode, this.userDetails.userName);
          if (this.otpDetails) {
            alert('OTP was sent successfully to registered mobile');
            this.timer();
          }
          this.hideMobileNumberform = false;
          this.otpFormDisplay = true;
        }
      }
    } catch (error) {
      alert(error);
    }
  }

  /**
   * @method verifyOtp
   * @description Validates the OTP entered by the user and verifies it against the sent OTP.
   * On successful verification, navigates to the password reset step (step 2).
   * 
   * @async
   * @returns {Promise<void>}
   * @throws {Error} Catches and alerts OTP verification errors from the service
   */
  public async verifyOtp() {
    try {
      this.otpSubmitted = true;
      // stop here if form is invalid
      if (this.otpForm.invalid) {
        return;
      }
      this.otpVerifyDetails = await this.userService.otpVerification(this.otpVerifyDetails.otp, this.userDetails.userName);
      if (this.otpVerifyDetails) {
        this.router.navigate(['/forgotpasswordstep2/' + this.userDetails.userName]);
      }
    } catch (error) {
      alert(error.error.message);
    }
  }

  /**
   * @method resendOtp
   * @description Handles OTP resend functionality. Retrieves updated user details and sends
   * a new OTP to the registered mobile number. Starts a new countdown timer for the resent OTP.
   * 
   * @async
   * @returns {Promise<void>}
   * @throws {Error} Catches and alerts any service errors during resend process
   */
  public async resendOtp() {
    try {
      this.submitted = true;
      // stop here if form is invalid
      if (this.forgotForm.invalid) {
        return;
      }
      this.userDetails = await this.userService.getUserDetailsByNumber(this.userDetails.userName);
      this.otpDetails = await this.userService.forgotPassword('+91', this.userDetails.userName);
      if (this.otpDetails) {
        alert('OTP was sent successfully to registered mobile');
        this.resendtimer();
      }
      this.hideMobileNumberform = false;
      this.otpFormDisplay = true;
    } catch (error) {
      alert(error);
    }
  }

  /**
   * @method timer
   * @description Starts a countdown timer for OTP expiration (2 minutes).
   * Updates the timer display every second and disables the resend button during countdown.
   * Automatically enables resend button after 121 seconds.
   * 
   * @returns {void}
   */
  timer() {
    let intervalId = setInterval(() => {
      const minutes = Math.floor(this.time / 60);
      let seconds: string | number = this.time % 60;
      seconds = seconds < 2 ? '0' + seconds : seconds;
      this.countDownEle = `${minutes}: ${seconds}`;
      this.time--;
      this.disabledResendOtp = true;
      this.hideTimer = true;
      this.resendhideTimer = false;
    }, 1000);
    setTimeout(() => {
      clearInterval(intervalId);
      this.disabledResendOtp = false;
      this.hideTimer = false;
      this.resendhideTimer = false;
    }, 121000);
  }

  /**
   * @method resendtimer
   * @description Starts a countdown timer for the resent OTP (2 minutes).
   * Updates the resend timer display every second and manages the disabled state of the resend button.
   * Automatically enables the button after countdown completes.
   * 
   * @returns {void}
   */
  resendtimer() {
    this.resendtime = 120; // Set the initial time in seconds

    let intervalId = setInterval(() => {
      const resendminutes = Math.floor(this.resendtime / 60);
      let resendseconds: string | number = this.resendtime % 60;
      resendseconds = resendseconds < 10 ? '0' + resendseconds : resendseconds;
      this.resendcountDownEle = `${resendminutes}:${resendseconds}`;
      this.resendtime--;

      if (this.resendtime < 0) {
        clearInterval(intervalId);
        this.disabledResendOtp = false;
        this.resendhideTimer = false;
      } else {
        this.disabledResendOtp = true;
        this.hideTimer = false;
        this.resendhideTimer = true;
      }
    }, 1000);
  }

}
